import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../../Configuration/axiosInstance";
import shipmentStylez from "../../../Components/Shipments/ShipmentCard/ShipmentCard.module.css";
import Select from "react-select";
import SuccessDialogue from "../../../Components/Shared/SuccessDialog/SuccessDialog";
import { FaTrash, FaPlus, FaMinus, FaShip } from "react-icons/fa";
import LoadingSpinner from "../../../Components/Shared/Loader/Loader";

const UpdateShipment = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [msg, setMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    blNumber: "",
    Vessel: "",
    containerNumber: "",
    sealNumber: "",
    etd: "",
    eta: "",
    unloadDate: "",
  });

  // ✅ NEW: State for managing multiple containers
  const [containers, setContainers] = useState([]);
  const [currentContainer, setCurrentContainer] = useState(0);
  const [isLegacyFormat, setIsLegacyFormat] = useState(false);

  // ✅ UPDATED: Legacy states (for backward compatibility)
  const [productArr, setProductArr] = useState([]);
  const [product, setProduct] = useState("");
  const [stock, setStock] = useState("");
  const [editedQuantities, setEditedQuantities] = useState({});
  const [prodOptions, setProdOptions] = useState([]);
  const [show, setShow] = useState(false);

  const productOptions = prodOptions?.map((i) => ({
    value: i._id,
    label: `${i.productName}-${i.sku}`,
  }));

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const [productResponse, shipmentResponse] = await Promise.all([
          axiosInstance.get("products/allproducts"),
          axiosInstance.get(`shipments/${location?.state?._id}`),
        ]);

        setProdOptions(productResponse.data.data);
        const shipmentData = shipmentResponse.data.data;
        setFormData(shipmentData);

        // ✅ NEW: Initialize containers based on data format
        if (shipmentData.containers && shipmentData.containers.length > 0) {
          // New format: multiple containers
          setContainers(
            shipmentData.containers.map((container, index) => ({
              id: container._id || index,
              containerNumber: container.containerNumber,
              vessel: container.vessel,
              sealNumber: container.sealNumber,
              items: container.items || [],
            }))
          );
          setIsLegacyFormat(false);
        } else {
          // Legacy format: single container
          setContainers([
            {
              id: 0,
              containerNumber: shipmentData.containerNumber || "",
              vessel: shipmentData.Vessel || "",
              sealNumber: shipmentData.sealNumber || "",
              items: shipmentData.items || [],
            },
          ]);
          setProductArr(shipmentData.items || []);
          setIsLegacyFormat(true);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [location?.state?._id]);

  // ✅ NEW: Container management functions
  const addContainer = () => {
    const newContainer = {
      id: Date.now(),
      containerNumber: "",
      vessel: "",
      sealNumber: "",
      items: [],
    };
    setContainers([...containers, newContainer]);
    setCurrentContainer(containers.length);
  };

  const removeContainer = (containerIndex) => {
    if (containers.length > 1) {
      const updatedContainers = containers.filter(
        (_, index) => index !== containerIndex
      );
      setContainers(updatedContainers);
      if (currentContainer >= updatedContainers.length) {
        setCurrentContainer(updatedContainers.length - 1);
      }
    }
  };

  const handleContainerChange = (containerIndex, field, value) => {
    const updatedContainers = containers.map((container, index) => {
      if (index === containerIndex) {
        return { ...container, [field]: value };
      }
      return container;
    });
    setContainers(updatedContainers);
  };

  // ✅ NEW: Product management for containers
  const addProductToContainer = () => {
    if (!product || !stock || stock <= 0) return;

    const selectedProduct = prodOptions.find((p) => p._id === product);
    if (!selectedProduct) return;

    const newItem = {
      product: selectedProduct._id,
      quantity: Number(stock),
    };

    const updatedContainers = containers.map((container, index) => {
      if (index === currentContainer) {
        const existingItemIndex = container.items.findIndex(
          (item) =>
            item.product === newItem.product ||
            item.product._id === newItem.product
        );

        if (existingItemIndex >= 0) {
          const updatedItems = [...container.items];
          updatedItems[existingItemIndex].quantity += newItem.quantity;
          return { ...container, items: updatedItems };
        } else {
          return { ...container, items: [...container.items, newItem] };
        }
      }
      return container;
    });

    setContainers(updatedContainers);
    setProduct("");
    setStock("");
  };

  const removeProductFromContainer = (containerIndex, productIndex) => {
    const updatedContainers = containers.map((container, index) => {
      if (index === containerIndex) {
        const updatedItems = container.items.filter(
          (_, itemIndex) => itemIndex !== productIndex
        );
        return { ...container, items: updatedItems };
      }
      return container;
    });
    setContainers(updatedContainers);
  };

  const handleContainerProductQuantityEdit = (
    containerIndex,
    productIndex,
    newQuantity
  ) => {
    const updatedContainers = containers.map((container, index) => {
      if (index === containerIndex) {
        const updatedItems = container.items.map((item, itemIndex) => {
          if (itemIndex === productIndex) {
            return { ...item, quantity: Number(newQuantity) || 0 };
          }
          return item;
        });
        return { ...container, items: updatedItems };
      }
      return container;
    });
    setContainers(updatedContainers);
  };

  // ✅ UPDATED: Form submission to handle both formats
  const formAction = async () => {
    setLoading(true);
    try {
      let updateData = { ...formData };

      if (isLegacyFormat) {
        // Legacy format: update items directly
        updateData.items = productArr;
      } else {
        // New format: update containers
        updateData.containers = containers.map((container) => ({
          containerNumber: container.containerNumber,
          vessel: container.vessel,
          sealNumber: container.sealNumber,
          items: container.items.map((item) => ({
            product: item.product._id || item.product,
            quantity: item.quantity,
          })),
        }));
      }

      await axiosInstance.patch(
        `shipments/${location?.state?._id}`,
        updateData
      );

      setLoading(false);
      setMsg("Shipment updated successfully ✅");
      setShow(true);
      setTimeout(() => {
        setShow(false);
        setMsg("");
        navigate("/shipments");
      }, 3000);
    } catch (error) {
      console.error("Error updating shipment:", error);
      setLoading(false);
      setMsg("Error updating shipment. Please try again.");
      setShow(true);
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleChange = (selectedOption) => {
    setProduct(selectedOption?.value || "");
  };

  // ✅ Legacy functions (for backward compatibility)
  const productAddOn = (newItem) => {
    const existingItemIndex = productArr.findIndex(
      (item) => item.product === newItem.product
    );
    if (existingItemIndex !== -1) {
      setProductArr((prevArr) =>
        prevArr.map((item, index) =>
          index === existingItemIndex
            ? { ...item, quantity: item.quantity + newItem.quantity }
            : item
        )
      );
    } else {
      setProductArr((prevArr) => [...prevArr, newItem]);
    }
  };

  const deleteData = (index) => {
    setProductArr((prevArr) =>
      prevArr.filter((item, currentIndex) => currentIndex !== index)
    );
  };

  const handleQuantityEdit = (index, newValue) => {
    setEditedQuantities((prevQuantities) => ({
      ...prevQuantities,
      [index]: newValue,
    }));

    setProductArr((prevArr) =>
      prevArr.map((item, currentIndex) =>
        currentIndex === index ? { ...item, quantity: newValue } : item
      )
    );
  };

  // ✅ NEW: Get product name helper
  const getProductName = (productId) => {
    const prod = prodOptions.find((p) => p._id === productId);
    return prod ? prod.productName : "Unknown Product";
  };

  return (
    <>
      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className={shipmentStylez.card}>
          <div className={shipmentStylez.cardHeader}>
            <h2>🚢 Update Shipment - {formData.blNumber}</h2>
            <div className={shipmentStylez.formatBadge}>
              {isLegacyFormat
                ? "📦 Legacy Format"
                : "🚢 Multi-Container Format"}
            </div>
          </div>

          <div className={shipmentStylez.card_body}>
            <SuccessDialogue show={show} msg={msg} />

            {/* Shipment Level Information */}
            <div className={shipmentStylez.shipmentSection}>
              <h3 className={shipmentStylez.sectionTitle}>
                📋 Shipment Information
              </h3>
              <div className={shipmentStylez.row}>
                <div className={shipmentStylez.labelStyle}>
                  <label>BL Number:</label>
                  <input
                    onChange={handleInput}
                    name="blNumber"
                    value={formData.blNumber}
                    className={shipmentStylez.inputStyle}
                  />
                </div>
                <div className={shipmentStylez.labelStyle}>
                  <label>Main Vessel:</label>
                  <input
                    onChange={handleInput}
                    name="Vessel"
                    value={formData.Vessel}
                    className={shipmentStylez.inputStyle}
                    placeholder="Primary vessel name"
                  />
                </div>
              </div>
              <div className={shipmentStylez.row}>
                <div className={shipmentStylez.labelStyle}>
                  <label>ETD:</label>
                  <input
                    onChange={handleInput}
                    type="date"
                    className={shipmentStylez.inputStyle}
                    name="etd"
                    value={
                      formData.etd
                        ? new Date(formData.etd).toLocaleDateString("en-CA")
                        : ""
                    }
                  />
                </div>
                <div className={shipmentStylez.labelStyle}>
                  <label>ETA:</label>
                  <input
                    onChange={handleInput}
                    type="date"
                    className={shipmentStylez.inputStyle}
                    name="eta"
                    value={
                      formData.eta
                        ? new Date(formData.eta).toLocaleDateString("en-CA")
                        : ""
                    }
                  />
                </div>
                <div className={shipmentStylez.labelStyle}>
                  <label>Unload Date:</label>
                  <input
                    onChange={handleInput}
                    type="date"
                    className={shipmentStylez.inputStyle}
                    name="unloadDate"
                    value={
                      formData.unloadDate
                        ? new Date(formData.unloadDate).toLocaleDateString(
                            "en-CA"
                          )
                        : ""
                    }
                  />
                </div>
              </div>
            </div>

            {/* ✅ NEW: Container Management Section */}
            {!isLegacyFormat && (
              <div className={shipmentStylez.containersSection}>
                <div className={shipmentStylez.containerHeader}>
                  <h3 className={shipmentStylez.sectionTitle}>
                    🚢 Containers ({containers.length})
                  </h3>
                  <button
                    type="button"
                    onClick={addContainer}
                    className={shipmentStylez.addContainerBtn}
                  >
                    <FaPlus /> Add Container
                  </button>
                </div>

                {/* Container Tabs */}
                <div className={shipmentStylez.containerTabs}>
                  {containers.map((container, index) => (
                    <button
                      key={container.id}
                      type="button"
                      className={`${shipmentStylez.containerTab} ${
                        currentContainer === index
                          ? shipmentStylez.activeTab
                          : ""
                      }`}
                      onClick={() => setCurrentContainer(index)}
                    >
                      <FaShip /> Container {index + 1}
                      {containers.length > 1 && (
                        <span
                          className={shipmentStylez.removeTabBtn}
                          onClick={(e) => {
                            e.stopPropagation();
                            removeContainer(index);
                          }}
                        >
                          <FaMinus />
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Current Container Form */}
                {containers[currentContainer] && (
                  <div className={shipmentStylez.containerForm}>
                    <div className={shipmentStylez.containerInfo}>
                      <h4 className={shipmentStylez.containerTitle}>
                        Container {currentContainer + 1} Details
                      </h4>
                      <div className={shipmentStylez.row}>
                        <div className={shipmentStylez.labelStyle}>
                          <label>Container Number:</label>
                          <input
                            type="text"
                            value={containers[currentContainer].containerNumber}
                            onChange={(e) =>
                              handleContainerChange(
                                currentContainer,
                                "containerNumber",
                                e.target.value
                              )
                            }
                            className={shipmentStylez.inputStyle}
                            placeholder="e.g., ABCD1234567"
                          />
                        </div>
                        <div className={shipmentStylez.labelStyle}>
                          <label>Vessel:</label>
                          <input
                            type="text"
                            value={containers[currentContainer].vessel}
                            onChange={(e) =>
                              handleContainerChange(
                                currentContainer,
                                "vessel",
                                e.target.value
                              )
                            }
                            className={shipmentStylez.inputStyle}
                            placeholder="Vessel name"
                          />
                        </div>
                        <div className={shipmentStylez.labelStyle}>
                          <label>Seal Number:</label>
                          <input
                            type="text"
                            value={containers[currentContainer].sealNumber}
                            onChange={(e) =>
                              handleContainerChange(
                                currentContainer,
                                "sealNumber",
                                e.target.value
                              )
                            }
                            className={shipmentStylez.inputStyle}
                            placeholder="Seal number"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Product Addition for Container */}
                    <div className={shipmentStylez.productSection}>
                      <h4 className={shipmentStylez.productTitle}>
                        📦 Products in Container {currentContainer + 1}
                      </h4>
                      <div className={shipmentStylez.productForm}>
                        <div className={shipmentStylez.labelStyle}>
                          <label>Product:</label>
                          <Select
                            className={shipmentStylez.select}
                            options={productOptions}
                            onChange={handleChange}
                            value={productOptions.find(
                              (opt) => opt.value === product
                            )}
                            placeholder="Select a product"
                          />
                        </div>
                        <div className={shipmentStylez.labelStyle}>
                          <label>Quantity:</label>
                          <input
                            type="number"
                            value={stock}
                            onChange={(e) => setStock(e.target.value)}
                            className={shipmentStylez.inputStyle}
                            min="1"
                            placeholder="Enter quantity"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={addProductToContainer}
                          className={shipmentStylez.addProductBtn}
                          disabled={!product || !stock || stock <= 0}
                        >
                          <FaPlus /> Add Product
                        </button>
                      </div>

                      {/* Container Products Table */}
                      {containers[currentContainer].items.length > 0 && (
                        <div className={shipmentStylez.productView}>
                          <table className={shipmentStylez.table}>
                            <thead>
                              <tr>
                                <th>Product Name</th>
                                <th>Quantity</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {containers[currentContainer].items.map(
                                (item, index) => (
                                  <tr key={index}>
                                    <td>
                                      {getProductName(
                                        item.product._id || item.product
                                      )}
                                    </td>
                                    <td>
                                      <input
                                        type="number"
                                        value={item.quantity}
                                        onChange={(e) =>
                                          handleContainerProductQuantityEdit(
                                            currentContainer,
                                            index,
                                            e.target.value
                                          )
                                        }
                                        className={shipmentStylez.quantityInput}
                                        min="0"
                                      />
                                    </td>
                                    <td>
                                      <button
                                        className={shipmentStylez.deleteBtn}
                                        onClick={() =>
                                          removeProductFromContainer(
                                            currentContainer,
                                            index
                                          )
                                        }
                                        title="Remove product"
                                      >
                                        <FaTrash />
                                      </button>
                                    </td>
                                  </tr>
                                )
                              )}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ✅ Legacy Product Management (for backward compatibility) */}
            {isLegacyFormat && (
              <div className={shipmentStylez.legacySection}>
                <h3 className={shipmentStylez.sectionTitle}>
                  📦 Legacy Container
                </h3>
                <div className={shipmentStylez.row}>
                  <div className={shipmentStylez.labelStyle}>
                    <label>Container Number:</label>
                    <input
                      onChange={handleInput}
                      name="containerNumber"
                      value={formData.containerNumber}
                      className={shipmentStylez.inputStyle}
                    />
                  </div>
                  <div className={shipmentStylez.labelStyle}>
                    <label>Seal Number:</label>
                    <input
                      onChange={handleInput}
                      name="sealNumber"
                      value={formData.sealNumber}
                      className={shipmentStylez.inputStyle}
                    />
                  </div>
                </div>

                <div className={shipmentStylez.productRow}>
                  <div className={shipmentStylez.labelStyle}>
                    <label>Product:</label>
                    <Select
                      className={shipmentStylez.select}
                      options={productOptions}
                      onChange={(selectedOption) =>
                        setProduct(selectedOption?.value || "")
                      }
                    />
                  </div>
                  <div className={shipmentStylez.labelStyle}>
                    <label>Quantity:</label>
                    <input
                      type="number"
                      value={stock}
                      onChange={(e) => setStock(Number(e.target.value))}
                      className={shipmentStylez.inputStyle}
                      min="1"
                    />
                  </div>
                  <button
                    className={shipmentStylez.addBtn}
                    onClick={() =>
                      productAddOn({
                        product: product,
                        quantity: Number(stock),
                      })
                    }
                    disabled={!product || !stock}
                  >
                    Add Products
                  </button>
                </div>

                {productArr?.length > 0 && (
                  <div className={shipmentStylez.productView}>
                    <table className={shipmentStylez.table}>
                      <thead>
                        <tr>
                          <th>Product Name</th>
                          <th>Quantity</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {productArr?.map((item, index) => {
                          const prod = prodOptions?.find(
                            (p) => p._id === item.product
                          );
                          return (
                            <tr key={index}>
                              <td>{prod?.productName || "Unknown Product"}</td>
                              <td>
                                <input
                                  type="number"
                                  value={
                                    editedQuantities[index] !== undefined
                                      ? editedQuantities[index]
                                      : item.quantity
                                  }
                                  onChange={(e) =>
                                    handleQuantityEdit(index, e.target.value)
                                  }
                                  className={shipmentStylez.quantityInput}
                                />
                              </td>
                              <td>
                                <button
                                  className={shipmentStylez.deleteBtn}
                                  onClick={() => deleteData(index)}
                                >
                                  <FaTrash />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Summary Section */}
            <div className={shipmentStylez.summary}>
              <h4 className={shipmentStylez.summaryTitle}>📊 Update Summary</h4>
              <div className={shipmentStylez.summaryStats}>
                <div className={shipmentStylez.stat}>
                  <span className={shipmentStylez.statLabel}>Format:</span>
                  <span className={shipmentStylez.statValue}>
                    {isLegacyFormat ? "Legacy" : "Multi-Container"}
                  </span>
                </div>
                <div className={shipmentStylez.stat}>
                  <span className={shipmentStylez.statLabel}>Containers:</span>
                  <span className={shipmentStylez.statValue}>
                    {isLegacyFormat ? 1 : containers.length}
                  </span>
                </div>
                <div className={shipmentStylez.stat}>
                  <span className={shipmentStylez.statLabel}>
                    Total Products:
                  </span>
                  <span className={shipmentStylez.statValue}>
                    {isLegacyFormat
                      ? productArr.length
                      : containers.reduce(
                          (total, container) => total + container.items.length,
                          0
                        )}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className={shipmentStylez.btnStyle}>
            <button
              className="confirmBtn"
              onClick={formAction}
              disabled={loading}
            >
              {loading ? "Updating..." : "🚢 Update Shipment"}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default UpdateShipment;
